import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { useRouter } from 'next/navigation';
import { PAGE_URL } from '@/constant/url';
import { useEffect, useMemo } from 'react';

type Options = {
  redirectOnForbidden?: boolean;
};

/**
 * Portal Admin 권한 확인 Hook
 * @param requiredRoles - 허용할 권한 (문자열 또는 문자열 배열)
 * @param options - 옵션 객체 (redirectOnForbidden: 권한 없을 시 리다이렉트 여부)
 */
const useHasRoles = (
  requiredRoles: string | string[],
  options: Options = {},
): boolean => {
  const router = useRouter();
  const myInfo = useAtomValue(MyInfoState);

  const { redirectOnForbidden = true } = options;

  // 권한 검증 로직 (Memoization으로 불필요한 연산 방지)
  const hasRole = useMemo(() => {
    // 입력값을 배열로 정규화
    const rolesArray = Array.isArray(requiredRoles)
      ? requiredRoles
      : [requiredRoles];

    // 내 권한 목록에 요구 권한 중 하나라도 있는지 확인 (some)
    return myInfo?.roles?.some((role) => rolesArray.includes(role)) ?? false;
  }, [myInfo?.roles, requiredRoles]);

  useEffect(() => {
    if (!hasRole && redirectOnForbidden) {
      router.replace(PAGE_URL.FORBIDDEN);
    }
  }, [hasRole, redirectOnForbidden, router]);

  return hasRole;
};

export default useHasRoles;
